import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  searchSettings,
  type SettingsSearchResult,
} from "@/components/settings/settingsSearchIndex";
import { ChevronRight } from "lucide-react";

/**
 * Search box for the settings page. Matches individual settings fields by label
 * or keyword and shows a dropdown of breadcrumb paths; selecting a result
 * navigates to that field's tab and scrolls to it.
 * @returns Settings search element
 */
export function SettingsSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const results = useMemo(() => searchSettings(query), [query]);
  const showResults = open && query.trim().length > 0;

  const handleSelect = (result: SettingsSearchResult) => {
    setQuery("");
    setOpen(false);
    navigate(`/settings?tab=${result.tab}#${result.anchorId}`);
  };

  return (
    <Command
      shouldFilter={false}
      className="relative max-w-md overflow-visible border shadow-sm"
    >
      <CommandInput
        value={query}
        onValueChange={(value) => {
          setQuery(value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        placeholder="Search settings"
        aria-label="Search settings"
      />
      {showResults && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
          <CommandList>
            <CommandEmpty>No settings found</CommandEmpty>
            {results.map((result) => (
              <CommandItem
                key={result.anchorId}
                value={result.anchorId}
                onSelect={() => handleSelect(result)}
                className="cursor-pointer"
              >
                <span className="text-muted-foreground">{result.tabLabel}</span>
                <ChevronRight className="text-muted-foreground" />
                <span className="font-medium">{result.label}</span>
              </CommandItem>
            ))}
          </CommandList>
        </div>
      )}
    </Command>
  );
}
